/** Thanks to https://abhiiishek07.medium.com/kanban-board-multi-list-drag-and-drop-with-react-beautiful-dnd-in-next-js-12b73c32e938 */

import { useEffect, useState } from "react";

/** Helpers */
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";

/** MUI */
import { Alert, Box, Collapse, Grid } from "@mui/material";

/** UI */
import Column from "./Column";

const INITIAL_COLUMN_ORDER = ["list", "rows", "cols"];

const INITIAL_COL_DATA = {
    list: { id: "list", title: "Lista", itemsOrder: [] },
    rows: { id: "rows", title: "Wiersz", itemsOrder: ["Zm", "JT"] },
    cols: { id: "cols", title: "Kolumna", itemsOrder: ["SC"] },
};

const ITEMS = { Zm: { id: "Zm", title: "Zm" }, JT: { id: "JT", title: "JT" }, SC: { id: "SC", title: "SC" } };

const Board = (props) => {
    const { layout, onChangeLayout } = props;
    const { t } = useTranslation();

    /** states */
    const [columnsOrder, setColumnsOrder] = useState(INITIAL_COLUMN_ORDER);
    const [data, setData] = useState(null);
    const [columnsDisabled, setColumnsDisabled] = useState([]);
    const [dragStarted, setDragStarted] = useState(false);
    const [selected, setSelected] = useState({ jt: false, zm: false, sc: false });
    const [showNote, setShowNote] = useState(false);

    /** effects */
    useEffect(() => {
        const _newColData = structuredClone(INITIAL_COL_DATA);
        INITIAL_COLUMN_ORDER.forEach((colId) => (_newColData[colId].itemsOrder = layout[colId]));
        setData(_newColData);
    }, [layout]);

    useEffect(() => {
        if (!data || !layout) return;
        const _newLayout = { cols: data.cols.itemsOrder, list: data.list.itemsOrder, rows: data.rows.itemsOrder };
        onChangeLayout && JSON.stringify(layout) !== JSON.stringify(_newLayout) && onChangeLayout(_newLayout);
    }, [data]);

    /** handlers */
    const checkDisabled = (props) => {
        const { from, to, id } = props || {};

        const _disabled = [];

        if (data?.list?.itemsOrder?.length >= 1 && (id ? !data?.list?.itemsOrder?.includes(id) : true))
            _disabled.push("list");

        if (id === "Zm") _disabled.push("cols");

        setColumnsDisabled(_disabled);
    };

    const handleOnFocused = (focus) => setShowNote(focus);

    const handleOnSelect = (item) => {
        const _selected = {
            ...selected,
            ...item,
        };

        if (JSON.stringify(selected) !== JSON.stringify(_selected)) {
            setSelected(_selected);

            const _dragStarted = Object.values(_selected)?.some((v) => v);
            setDragStarted(_dragStarted);

            _dragStarted || checkDisabled();
        }
    };

    const handleDragStart = (start, provided) => {
        const { draggableId: currentItem, source } = start;
        const { droppableId: from } = source;

        checkDisabled({ from, id: currentItem });

        provided.announce(
            t(
                `Zaznaczono element {{itemName}} na pozycji {{startPosition}} z {{listLength}} w sekcji {{listName}}{{dot}}`,
                {
                    itemName: t(currentItem),
                    startPosition: start.source.index + 1,
                    listLength: data?.[from]?.itemsOrder?.length,
                    listName: t(data?.[from]?.title),
                    dot: ".",
                }
            )
        );
    };

    const handleDragUpdate = (update, provided) => {
        const { draggableId: currentItem, source, destination } = update;
        const { droppableId: from } = source;
        const { droppableId: to } = destination || {};

        checkDisabled({ from, to, id: currentItem });

        const message = update.destination
            ? t(`Przesunięto element {{itemName}} do pozycji {{startPosition}} w sekcji {{listName}}{{dot}}`, {
                  itemName: t(currentItem),
                  startPosition: update.destination.index + 1,
                  listLength: data?.[to]?.itemsOrder?.length,
                  listName: t(data?.[to]?.title),
                  dot: ".",
              })
            : t(`Obecnie nie jesteś na obszarze, na który można przeciągać{{dot}}`, { dot: "." });

        provided.announce(message);
    };

    const handleDragEnd = (results, provided) => {
        const { draggableId: currentItem, source, destination, type } = results;
        const { droppableId: from, index: fromIndex } = source;
        const { droppableId: to, index: toIndex } = destination || {};

        let message = "";

        if ((from !== to || fromIndex !== toIndex) && toIndex) {
            message += t(`Przeniesiono {{itemName}} do pozycji {{toIndex}} w sekcji {{listName}}{{dot}}`, {
                itemName: t(currentItem),
                fromIndex: fromIndex + 1,
                toIndex: toIndex + 1,
                listName: t(data?.[to]?.title),
                dot: ".",
            });
        } else {
            message += t("Odznaczono {{itemName}}{{dot}} ", { itemName: t(currentItem), dot: "." });
        }

        provided.announce(message);

        if (!destination) return;

        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const sourceIndex = source.index;
        const destinationIndex = destination.index;

        if (type === "COLUMN") {
            //dragging the columns
            const reorderedColumns = [...columnsOrder];
            const [removedItem] = reorderedColumns.splice(sourceIndex, 1);
            reorderedColumns.splice(destinationIndex, 0, removedItem);

            setColumnsOrder(reorderedColumns);
            //save the reordered column in database

            return;
        } else {
            //changes within same column

            if (source.droppableId === destination.droppableId) {
                const source_col_id = source.droppableId;
                const new_items_id_collection = [...data[source_col_id].itemsOrder];
                const [deleted_item_id] = new_items_id_collection.splice(sourceIndex, 1);
                new_items_id_collection.splice(destinationIndex, 0, deleted_item_id);
                const new_data = { ...data };
                new_data[source_col_id].itemsOrder = new_items_id_collection;
                setData(new_data);

                //update the db
            } else {
                //changes within different col
                const source_col_id = source.droppableId,
                    dest_col_id = destination.droppableId;

                const new_source_items_id_collc = [...data[source_col_id].itemsOrder];
                const new_dest_items_id_collc = [...data[dest_col_id].itemsOrder];
                const [deleted_item_id] = new_source_items_id_collc.splice(sourceIndex, 1);

                new_dest_items_id_collc.splice(destinationIndex, 0, deleted_item_id);
                const new_data = { ...data };
                new_data[source_col_id].itemsOrder = new_source_items_id_collc;
                new_data[dest_col_id].itemsOrder = new_dest_items_id_collc;

                setData(new_data);

                //update the db
            }
        }
    };

    return (
        <Box sx={{ mt: 1 }}>
            {/* Set up DragDropContext */}
            <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart} onDragUpdate={handleDragUpdate}>
                {/* Render Droppable area for columns */}
                <Droppable droppableId="ROOT" type="COLUMN" direction="HORIZONTAL">
                    {(provided) => {
                        return (
                            <Grid container spacing={2} {...provided.droppableProps} ref={provided.innerRef}>
                                {/* Map through columnsOrder to render each column */}
                                {!!data &&
                                    columnsOrder.map((colId, index) => {
                                        const columnData = data[colId];
                                        return (
                                            <Grid item xs={12} lg={4} key={columnData.id} sx={{ position: "relative" }}>
                                                <Column
                                                    {...columnData}
                                                    ITEMS={ITEMS}
                                                    columnsDisabled={columnsDisabled}
                                                    dragStarted={dragStarted}
                                                    onSelect={handleOnSelect}
                                                    onFocused={(focused) => handleOnFocused(focused)}
                                                />
                                            </Grid>
                                        );
                                    })}
                                {provided.placeholder}
                            </Grid>
                        );
                    }}
                </Droppable>
            </DragDropContext>

            <Collapse in={showNote}>
                <Alert
                    role={"note"}
                    severity={"info"}
                    sx={{ mt: 3 }}
                    id={"rdnd-note"}
                    aria-label={t(
                        `\n - Naciśnij spację, aby rozpocząć lub zakończyć przeciąganie elementu{{dot}} 
                        Użyj klawiszy strzałek do przeciągania elementu między sekcjami oraz do zmiany pozycji elementu w ramach sekcji{{dot}} 
                        Dwukrotnie naciśnij klawisz escape, aby anulować dokonane przeciągnięcie{{dot}} 
                        Użyj przycisków TAB lub SHIFT + TAB, aby zmienić wybór elementu który chcesz przeciągnąć{{dot}} 
                        Sekcja "Lista" może zawierać maksymalnie jeden element{{dot}} Element "Zmienna" nie może zostać przesunięty do sekcji "Kolumna"`,
                        { dot: "." }
                    )}
                >
                    {t(
                        `Naciśnij spację, aby rozpocząć lub zakończyć przeciąganie elementu{{dot}} 
                        Użyj klawiszy strzałek do przeciągania elementu między sekcjami oraz do zmiany pozycji elementu w ramach sekcji{{dot}} 
                        Naciśnij klawisz escape, aby anulować dokonane przeciągnięcie{{dot}} 
                        Użyj przycisków TAB lub SHIFT + TAB, aby zmienić wybór elementu który chcesz przeciągnąć{{dot}} 
                        Sekcja "Lista" może zawierać maksymalnie jeden element{{dot}} Element "Zmienna" nie może zostać przesunięty do sekcji "Kolumna"`,
                        { dot: "." }
                    )}
                </Alert>
            </Collapse>
            
        </Box>
    );
};

export default Board;

// Don't touch!
// t("Zm")
// t("JT")
// t("SC")


